import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Moment } from 'moment/moment';
import { getDatesRange } from '@mgm/common/utils';
import { QUERY_KEYS } from '../../utils/enums';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { OrdersController } from '../../services/orders.api';
import { ordersActions } from '../../store/orders/slice';
import { useError } from '../useCommon';
import { IBaseResponse, TAxiosError } from '../../types/responses';
import { TExchangeVariantReq } from '../../types/requests';
import { TExchangeVariantRes } from '../../types/responses/orders.type';

export function useRecalculatePlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const queryClient = useQueryClient();

  const { handleReqError } = useError();

  const recalculatePlanApi = (payload: {
    commissionPlanId: number,
    orderIds: Array<string>
  }) => {
    const data = {
      shopName: personnel.shopName,
      commissionPlanId: payload.commissionPlanId,
      orderIds: payload.orderIds,
    };

    return OrdersController().recalculatePlan<IBaseResponse>(data);
  };

  return useMutation(recalculatePlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS.GET_ORDERS] });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! recalculate plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! recalculate plan request threw an Exception! ${error}`);
    },
  });
}

type TUseGetScheduleAPIOptions = {
  dates?: [Moment, Moment],
  isMainPage?: boolean
};

export function useGetOrdersCountAPI(options: TUseGetScheduleAPIOptions) {
  const { personnel, shop } = useAppSelector(getAuthenticateUser);
  const byWeek = !options?.dates;

  const { fromDate, toDate } = getDatesRange(
    options?.dates,
    byWeek,
    false,
    shop?.shopTimezone,
  );
  const { handleReqError } = useError();
  const dispatch = useAppDispatch();

  return useQuery(
    [QUERY_KEYS.ORDERS.GET_ORDERS_LENGTH],
    async () => {
      const data = {
        shopName: personnel.shopName,
        timezone: shop?.shopTimezone,
        from: fromDate,
        to: toDate,
      };

      const rsp = await OrdersController().getOrdersCount<IBaseResponse & {
        ordersCount: number
      }>(data);

      if (rsp?.success && 'ordersCount' in rsp) {
        dispatch(ordersActions.setOrderCounts(rsp.ordersCount));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get order counts failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      suspense: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      enabled: options?.isMainPage || true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
    },
  );
}

export function useExchangeVariantAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();

  const { handleReqError } = useError();

  const exchangeVariantApi = (payload: Omit<TExchangeVariantReq, 'shopName'>) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return OrdersController().exchangeVariant<TExchangeVariantRes>(data);
  };

  return useMutation(exchangeVariantApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success && 'exchangeResult' in rsp) {
        dispatch(ordersActions.setVariantExchangeResult(rsp.exchangeResult));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! exchange variant failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! exchange variant request threw an Exception! ${error}`);
    },
  });
}
